import { CalendarDaysIcon } from "@heroicons/react/24/outline";
import { useEffect, useMemo, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { Calendar } from "@/core/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/core/components/ui/popover";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { dayjs, formatDateRange } from "@/core/utils/date";
import { cn } from "@/core/utils/utils";
import type { IcmPeriod } from "@/icm/types";
import { PayPeriodLockedMark } from "@/payroll/pay-periods/pay-period-locked";
import { PAYROLL_RUN_STATUS_UI } from "@/payroll/pay-periods/payroll-run-status-ui";

interface IcmPeriodPickerProps {
  periods: IcmPeriod[];
  calendarPeriods: IcmPeriod[];
  selectedPeriodStart: string | undefined;
  onSelect: (periodStart: string) => void;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
}

const parseLocalDate = (dateStr: string): Date => {
  const d = dayjs(dateStr);
  return new Date(d.year(), d.month(), d.date());
};

const PERIOD_STATUS_DOT_CLASSNAME = {
  secondary: "bg-stroke-secondary",
  gray: "bg-gray-40",
  blue: "bg-blue-50",
  green: "bg-green-50",
  destructive: "bg-red-50",
  gold: "bg-gold-20",
} as const;

export const IcmPeriodPicker = ({
  periods,
  calendarPeriods,
  selectedPeriodStart,
  onSelect,
  isLoading,
  disabled,
  className,
}: IcmPeriodPickerProps) => {
  const [open, setOpen] = useState(false);
  const [hoveredIdx, setHoveredIdx] = useState<number | null>(null);
  const [month, setMonth] = useState<Date>(new Date());
  const selectedItemRef = useRef<HTMLButtonElement>(null);

  const selectedPeriod = periods.find(
    (p) => p.period_start === selectedPeriodStart,
  );
  const selectedIdx = selectedPeriod ? periods.indexOf(selectedPeriod) : -1;

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      const target = selectedPeriod ?? (periods.length > 0 ? periods[0] : null);
      const targetDate = target
        ? parseLocalDate(target.period_start)
        : new Date();
      // Subtract one month so the target month appears on the right (right = current/recent, left = previous)
      setMonth(dayjs(targetDate).subtract(1, "month").toDate());
    }
    setHoveredIdx(null);
    setOpen(nextOpen);
  };

  // Narrow calendarPeriods to the two visible months ± 1 month buffer so
  // react-day-picker only evaluates ~14-18 modifier ranges on each hover render.
  // Anchors to `month` (the left panel) rather than the year to correctly
  // handle December/January cross-year views.
  const visibleCalendarPeriods = useMemo(() => {
    const rangeStart = dayjs(month).subtract(1, "month");
    const rangeEnd = dayjs(month).add(2, "months");
    return calendarPeriods.filter(
      (p) =>
        !dayjs(p.period_end).isBefore(rangeStart) &&
        !dayjs(p.period_start).isAfter(rangeEnd),
    );
  }, [calendarPeriods, month]);

  // O(1) date→period-index map built from the visible slice only.
  const dateToCalendarIdx = useMemo(() => {
    const map = new Map<string, number>();
    visibleCalendarPeriods.forEach((p, i) => {
      let d = dayjs(p.period_start);
      const end = dayjs(p.period_end);
      while (!d.isAfter(end)) {
        map.set(d.format("YYYY-MM-DD"), i);
        d = d.add(1, "day");
      }
    });
    return map;
  }, [visibleCalendarPeriods]);

  // Scroll the selected period into view when the popover opens
  useEffect(() => {
    if (open && selectedItemRef.current) {
      selectedItemRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  // Clear hover when the visible period slice changes (year navigation)
  useEffect(() => {
    setHoveredIdx(null);
  }, [visibleCalendarPeriods]);

  const modifiers: Record<string, DateRange | Date> = {};
  const modifiersClassNames: Record<string, string> = {};

  visibleCalendarPeriods.forEach((p, i) => {
    modifiers[`icm_p${i}`] = {
      from: parseLocalDate(p.period_start),
      to: parseLocalDate(p.period_end),
    };
  });

  if (hoveredIdx !== null) {
    const hp = visibleCalendarPeriods[hoveredIdx];
    const isSelected = hp.period_start === selectedPeriodStart;
    const from = parseLocalDate(hp.period_start);
    const to = parseLocalDate(hp.period_end);
    if (!isSelected) {
      modifiers["hover_start"] = from;
      modifiers["hover_end"] = to;
      modifiersClassNames["hover_start"] = "bg-accent rounded-s-full";
      modifiersClassNames["hover_end"] = "bg-accent rounded-e-full";

      const midFrom = new Date(from);
      midFrom.setDate(midFrom.getDate() + 1);
      const midTo = new Date(to);
      midTo.setDate(midTo.getDate() - 1);
      if (midFrom <= midTo) {
        modifiers["hover_mid"] = { from: midFrom, to: midTo };
        modifiersClassNames["hover_mid"] = "bg-accent rounded-none";
      }
    }
  }

  const selectedRange: DateRange | undefined = selectedPeriod
    ? {
        from: parseLocalDate(selectedPeriod.period_start),
        to: parseLocalDate(selectedPeriod.period_end),
      }
    : undefined;

  const hoveredPeriod =
    hoveredIdx !== null ? visibleCalendarPeriods[hoveredIdx] : null;

  const handleDayMouseEnter = (day: Date) => {
    const key = dayjs(day).format("YYYY-MM-DD");
    const idx = dateToCalendarIdx.get(key) ?? -1;
    setHoveredIdx(idx >= 0 ? idx : null);
  };

  const handleDayClick = (day: Date) => {
    const key = dayjs(day).format("YYYY-MM-DD");
    const idx = dateToCalendarIdx.get(key) ?? -1;
    if (idx >= 0) {
      onSelect(visibleCalendarPeriods[idx].period_start);
      setOpen(false);
    }
  };

  const handleListSelect = (period: IcmPeriod) => {
    onSelect(period.period_start);
    setOpen(false);
  };

  const triggerText = selectedPeriod
    ? formatDateRange(selectedPeriod.period_start, selectedPeriod.period_end)
    : null;

  const isDisabled = disabled || isLoading;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isDisabled}
          className={cn(
            "flex h-10 w-full items-center gap-2 rounded-lg border border-input bg-card px-3 shadow-sm transition-colors",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
            "cursor-pointer hover:border-ring",
            "disabled:cursor-not-allowed disabled:border-stroke-tertiary disabled:bg-bg-secondary disabled:text-tertiary-text",
            className,
            {
              "text-tertiary-text": !triggerText,
              "border-ring ring-1 ring-ring": open,
            },
          )}
        >
          {/* Text with a trailing calendar icon so the field reads as an
              interactive date selector, not static text. */}
          <span className="flex-1 truncate text-left text-sm">
            {triggerText ?? "Select a period..."}
          </span>
          <CalendarDaysIcon
            className={cn("size-5 shrink-0 text-secondary-text", {
              "text-interactive-text": open,
            })}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-auto p-0"
        align="end"
        sideOffset={8}
        collisionPadding={16}
      >
        <Stack direction="row">
          {/* Period list */}
          <Stack
            direction="column"
            gap="none"
            className="max-h-[320px] w-52 overflow-y-auto border-r"
          >
            {periods.map((p, i) => {
              const isSelected = i === selectedIdx;
              const statusDotVariant = p.latest_icm_run_status
                ? PAYROLL_RUN_STATUS_UI[p.latest_icm_run_status]
                    .timelineDotVariant
                : undefined;
              const statusDotClassName = statusDotVariant
                ? PERIOD_STATUS_DOT_CLASSNAME[statusDotVariant]
                : null;
              return (
                <button
                  key={p.period_start}
                  ref={isSelected ? selectedItemRef : null}
                  type="button"
                  onClick={() => handleListSelect(p)}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 px-4 py-2.5 text-left text-sm transition-colors hover:bg-accent",
                    { "bg-accent font-medium": isSelected },
                  )}
                >
                  <span>{formatDateRange(p.period_start, p.period_end)}</span>
                  {p.is_locked ? (
                    <PayPeriodLockedMark />
                  ) : p.has_payroll_run ? (
                    <div
                      className={cn(
                        "h-2.5 w-2.5 shrink-0 rounded-lg bg-primary",
                        {
                          [statusDotClassName ?? ""]: !!statusDotClassName,
                        },
                      )}
                    />
                  ) : null}
                </button>
              );
            })}
          </Stack>

          {/* Calendar + info strip */}
          <Stack
            direction="column"
            gap="none"
            onMouseLeave={() => setHoveredIdx(null)}
          >
            <Calendar
              mode="range"
              numberOfMonths={2}
              month={month}
              onMonthChange={setMonth}
              selected={selectedRange}
              onSelect={() => {}}
              showYearSwitcher={true}
              modifiers={modifiers}
              modifiersClassNames={modifiersClassNames}
              onDayMouseEnter={handleDayMouseEnter}
              onDayClick={handleDayClick}
            />
            <Stack direction="row" align="center" className="h-9 border-t px-3">
              <Typography variant="captionW1SemiBold" textColor="secondary">
                {hoveredPeriod
                  ? formatDateRange(
                      hoveredPeriod.period_start,
                      hoveredPeriod.period_end,
                    )
                  : selectedPeriod
                    ? formatDateRange(
                        selectedPeriod.period_start,
                        selectedPeriod.period_end,
                      )
                    : "Hover a date to see its period"}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </PopoverContent>
    </Popover>
  );
};
